import {OffersParameters} from '@findhotel/sapi/dist/types/packages/core/src/offers'
import {SearchHandler} from '@findhotel/sapi/dist/types/packages/core/src/search'
import {createAction} from '@reduxjs/toolkit'
import {getLocalTaxesIncluded, getTaxesIncluded} from 'modules/meta/selectors'
import {getUrlParams} from 'modules/search/selectors'
import {omit} from 'ramda'
import {isDateStringYesterday} from 'utils/dates'

import {calculateTotalRate} from '@vio/core/src/offer/business/calculateTotalRate'
import {SearchApiError} from '@vio/core/src/sapi/services/searchApi/error'
import {logger} from '@vio/core/src/utils/logging/logger'

import {AnchorNotFoundError} from '../errors'
import {
  anchorOffersReceivedWithMapping,
  offersReceivedWithMapping
} from '../offersMapping'
import {
  type AnchorPreheatOptions,
  createAnchorContinuationClient
} from '../partialSearch'
import {
  getHotelIdsLastPage,
  getHotelOfferEntitiesHaveOffers,
  getRequestedOffset,
  getRequestedResultsCount,
  getSearchId,
  getSearchParameters
} from '../selectors'
import {
  anchorOffersComplete,
  anchorReceived,
  checkSearchCompleted,
  hotelOffersComplete,
  hotelsReceived,
  moreHotelsRequested,
  newSearch,
  offersRequested,
  searchStarted,
  setNextPage,
  setNextSapiPage
} from '../slice'

import type {ApiSearchParameters, SapiClient} from '@findhotel/sapi'
import type {AppThunk, RootState} from 'store'

export type OnAnchorReceived = Parameters<
  SapiClient['search']
>[1]['onAnchorReceived']

const isValidAnchorParams = (
  params: ApiSearchParameters
): params is OffersParameters => !!params.hotelId

// if offers are empty we remove dates from the url to kick off another search for SAPI to handle the default dates
const handleEmptyResultsYesterday = (state: RootState) => {
  const urlParams = getUrlParams(state)
  const searchParameters = getSearchParameters(state)
  if (
    urlParams.checkIn &&
    isDateStringYesterday(searchParameters.checkIn) &&
    !getHotelOfferEntitiesHaveOffers(state)
  ) {
    const url = new URL(window.location.href)
    url.searchParams.delete('checkIn')
    url.searchParams.delete('checkOut')
    window.location.href = url.toString()
  }
}

const DESTINATION_PARAMS = [
  'hotelId',
  'placeId',
  'geolocation',
  'query',
  'boundingBox',
  'address',
  'iataCode'
] as const
const stripDestinationParams = omit(DESTINATION_PARAMS)

export const searchContinued = createAction<{
  searchId: string
  searchParameters: ApiSearchParameters
}>('sapiSearch/searchContinued')

type runHotelsSearchResult = Awaited<ReturnType<SearchHandler>> & {
  searchId: string
}
/**
 * Run a search for hotels (no offers)
 *
 * @remarks
 * - This can be used to preload search state that should be continued with `runSplitSearch`
 *
 * @param searchParameters - parameters to search for
 * @param userIp - userIp for server-side searches, empty for client-side (determined by SAPI backened)
 * @param onAnchorReceived - callback for anchorReceived event
 */
export const runHotelsSearch =
  ({
    searchParameters,
    userIp,
    onAnchorReceived
  }: {
    searchParameters: ApiSearchParameters
    userIp?: string
    onAnchorReceived?: OnAnchorReceived
  }): AppThunk<Promise<runHotelsSearchResult>> =>
  async (dispatch, getState, {getSapiClient}) => {
    const sapiClient = await getSapiClient()

    const isPagination = !!searchParameters.offset
    const parameters = {...searchParameters, userIp, skipOffers: true}

    let currentSearchId = getSearchId(getState()) // Undefined for a new search, defined for pagination

    // A new search may be triggered while this one is in progress, reject callbacks if so
    const shouldReject = () => currentSearchId !== getSearchId(getState())

    try {
      const searchResponse = await sapiClient.search(parameters, {
        onStart: response => {
          if (isPagination) return
          currentSearchId = response.searchId

          dispatch(searchStarted(response))
        },
        onAnchorReceived: response => {
          if (isPagination) return
          if (shouldReject()) return

          const {anchor, searchParameters} = response
          if (
            anchor.objectID === undefined &&
            searchParameters.boundingBox === undefined
          ) {
            throw new AnchorNotFoundError()
          }

          dispatch(anchorReceived(response))

          onAnchorReceived?.(response)
        },
        onHotelsReceived: response => {
          if (shouldReject()) return
          dispatch(hotelsReceived(response))
          dispatch(offersRequested())
        }
      })

      return {
        ...searchResponse,
        searchId: currentSearchId
      }
    } catch (error) {
      if (error instanceof AnchorNotFoundError) throw error
      throw new SearchApiError('Error fetching hotels', error)
    }
  }

/**
 * Run a search for anchor hotel offers
 * Anchor hotel may be preloaded server-side
 *
 * @param sapiClient - SAPI client instance
 */
const runAnchorOffersSearch =
  (
    searchId: string,
    continuationClient?: SapiClient
  ): AppThunk<Promise<unknown>> =>
  async (dispatch, getState, {getSapiClient}) => {
    // A new search may be triggered while this one is in progress, reject callbacks if so
    const shouldReject = () => searchId !== getSearchId(getState())
    if (shouldReject()) return

    const sapiClient = continuationClient ?? (await getSapiClient())
    if (shouldReject()) return

    const state = getState()
    const searchParameters = getSearchParameters(state)

    const parameters = {
      ...searchParameters,
      optimizeRooms: searchParameters.optimizeAnchorRooms,
      isAnchor: true,
      searchId
    }

    // Just a type-guard
    if (!isValidAnchorParams(parameters)) {
      logger.warn('Tried to fetch anchor offers for non-anchor search')
      return
    }

    try {
      await sapiClient.offers(parameters, {
        onOffersReceived: offerEntity => {
          if (shouldReject()) return
          dispatch(anchorOffersReceivedWithMapping(offerEntity))
        },
        onComplete: () => {
          if (shouldReject()) return
          dispatch(anchorOffersComplete())
          dispatch(checkSearchCompleted())
        }
      })
    } catch (error) {
      throw new SearchApiError('Error fetching anchor offers', error)
    }
  }

/**
 * Run an offers search for the latest page of hotels
 * Hotels may be preloaded server-side
 *
 * @param searchParameters - parameters to search for
 * @param sapiClient - SAPI client instance
 */
const runOffersSearch =
  (
    searchId: string,
    continuationClient?: SapiClient
  ): AppThunk<Promise<unknown>> =>
  async (dispatch, getState, {getSapiClient}) => {
    // A new search may be triggered while this one is in progress, reject callbacks if so
    const shouldReject = () => searchId !== getSearchId(getState())
    if (shouldReject()) return

    const sapiClient = continuationClient ?? (await getSapiClient())
    if (shouldReject()) return

    const state = getState()
    const searchParameters = getSearchParameters(state)
    const hotelIds = getHotelIdsLastPage(state)

    const parameters = {
      ...stripDestinationParams(searchParameters),
      searchId,
      hotelIds
    }

    // Place exists but no hotels - complete immediately without retrying
    if (hotelIds.length === 0) {
      dispatch(hotelOffersComplete())
      dispatch(checkSearchCompleted())
      return
    }

    const onComplete = () => {
      if (shouldReject()) return

      dispatch(hotelOffersComplete())
      dispatch(checkSearchCompleted())

      handleEmptyResultsYesterday(getState())
    }

    try {
      return sapiClient.hotelsOffers(parameters, {
        onOffersReceived: hotelOfferEntities => {
          if (shouldReject()) return

          dispatch(offersReceivedWithMapping({hotelOfferEntities}))
        },
        onComplete
      })
    } catch (error) {
      throw new SearchApiError('Error fetching offers', error)
    }
  }

/**
 * Run a client-side split search, fetching hotels and offers as needed
 *
 * @params options
 * @params options.searchParameters - Parameters to search with
 * @params options.hasPreloadedHotels - Whether we already have hotels in the state for this search
 */
export const runSplitSearch =
  ({
    searchParameters,
    hasPreloadedHotels,
    preloadedAnchorOptions
  }: {
    searchParameters: ApiSearchParameters
    hasPreloadedHotels: boolean
    preloadedAnchorOptions?: AnchorPreheatOptions
  }): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const hasAnchorHotel = !!searchParameters.hotelId
    const fetchAnchorOffers = (searchId: string) =>
      dispatch(runAnchorOffersSearch(searchId))
    const fetchOffers = (searchId: string) =>
      dispatch(runOffersSearch(searchId))

    if (preloadedAnchorOptions) {
      // Keep the server's normalized dates and search ID for offer reuse.
      const searchId = getSearchId(getState())
      const serverParameters = getSearchParameters(getState())

      dispatch(searchContinued({searchId, searchParameters: serverParameters}))

      const state = getState()
      const client = await createAnchorContinuationClient({
        ...preloadedAnchorOptions,
        getTotalRate: rate =>
          calculateTotalRate(
            rate,
            getTaxesIncluded(state),
            getLocalTaxesIncluded(state)
          )
      })

      if (getSearchId(getState()) !== searchId) return

      const remainingHotels = async () => {
        const response = await client.searchHotels(
          {...serverParameters, searchId},
          {skipConfig: true}
        )

        if (getSearchId(getState()) !== searchId) return

        dispatch(hotelsReceived(response))
        dispatch(offersRequested())

        await dispatch(runOffersSearch(searchId, client))
      }

      try {
        // Anchor offers must not wait for the remaining hotel search.
        await Promise.all([
          dispatch(runAnchorOffersSearch(searchId, client)),
          remainingHotels()
        ])
      } catch (error) {
        if (getSearchId(getState()) !== searchId) return

        throw error
      }
    } else if (hasPreloadedHotels) {
      const searchId = getSearchId(getState())
      dispatch(searchContinued({searchId, searchParameters}))

      // Hotels were preloaded by the backend, fetch offers
      hasAnchorHotel
        ? Promise.all([fetchAnchorOffers(searchId), fetchOffers(searchId)])
        : fetchOffers(searchId)
    } else {
      // No hotel data loaded yet, so start a fresh search
      // Try to fetch anchor offers as early as possible
      dispatch(newSearch())

      const {searchId} = await dispatch(
        runHotelsSearch({
          searchParameters,
          onAnchorReceived: ({searchId}) => {
            if (hasAnchorHotel) fetchAnchorOffers(searchId)
          }
        })
      )
      fetchOffers(searchId)
    }
  }

// Added as part of feature-srp-refresh-price
/**
 * Fetch new offers for the hotels already loaded
 *
 * @remarks
 * Unlike `runSplitSearch` with preloaded hotels, this waits for the offers, so
 * a failed request rejects and the caller can keep the old prices on screen.
 */
export const refetchOffers =
  (searchParameters: ApiSearchParameters): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const searchId = getSearchId(getState())
    dispatch(searchContinued({searchId, searchParameters}))

    // Wait for both, so the request that's still running can't complete the
    // search after the caller has handled the other one failing
    const results = await Promise.allSettled([
      searchParameters.hotelId && dispatch(runAnchorOffersSearch(searchId)),
      dispatch(runOffersSearch(searchId))
    ])

    const failure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )
    // A newer search owns the state now, so this failure must not change it
    if (failure && searchId === getSearchId(getState())) throw failure.reason
  }

/**
 * Load more results (pagination)
 *
 * @param options
 * @param options.paginate - whether to immediately show these results or only prefetch the data
 */
export const loadMoreResults =
  ({paginate = true}: {paginate?: boolean}): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const state = getState()
    const searchParameters = getSearchParameters(state)
    const searchId = getSearchId(state)

    const requestedOffset = getRequestedOffset(state)
    const requestedResultsCount = getRequestedResultsCount(state)
    const offset = requestedOffset + requestedResultsCount

    const parameters = {...searchParameters, searchId, offset}

    dispatch(moreHotelsRequested())

    // Due to performance issues, rendering the results will cause the page to be unresponsive, leaving the user in limbo
    // Hack it by setting a short timeout before changing offset and requesting results so we can render the loading state before rendering the changed results.
    window.setTimeout(async () => {
      if (paginate) dispatch(setNextPage())
      // Pagination needs this for every user, so keep it when removing feature-srp-refresh-price
      dispatch(setNextSapiPage())
      await dispatch(runHotelsSearch({searchParameters: parameters}))
      await dispatch(runOffersSearch(searchId))
    }, 1)
  }
